// ══════════════════════════════════════════════════════════════════
// src/utils/useToast.js
// Toast negro bottom-center igual al de ListingDetail
// Uso: this.$toast.success("Mensaje") | this.$toast.error("Mensaje")
// ══════════════════════════════════════════════════════════════════
 
const TOAST_DURATION = 2800;

// Mensajes actualmente visibles (para no apilar toasts idénticos).
const _activeMessages = new Set();

function ensureContainer() {
  if (document.getElementById("app-toast-container")) return;
  const el = document.createElement("div");
  el.id = "app-toast-container";
  Object.assign(el.style, {
    position:   "fixed",
    bottom:     "28px",
    left:       "50%",
    transform:  "translateX(-50%)",
    display:    "flex",
    flexDirection: "column",
    alignItems: "center",
    gap:        "10px",
    zIndex:     "99999",
    pointerEvents: "none",
  });
  document.body.appendChild(el);
}
 
function createToast({ message, type = "default", duration = TOAST_DURATION }) {
  // Evita apilar toasts idénticos (mismo tipo + mensaje) que ya están en pantalla.
  const dedupeKey = `${type}::${message}`;
  if (_activeMessages.has(dedupeKey)) return;
  _activeMessages.add(dedupeKey);

  ensureContainer();
  const container = document.getElementById("app-toast-container");

  const toast = document.createElement("div");
 
  // Estilos base
  const colors = {
    default: { bg: "#0f172a", color: "#fff",     icon: checkIcon() },
    success: { bg: "#0f172a", color: "#fff",     icon: checkIcon() },
    error:   { bg: "#991b1b", color: "#fff",     icon: xIcon() },
    warning: { bg: "#92400e", color: "#fff",     icon: warnIcon() },
    info:    { bg: "#1e40af", color: "#fff",     icon: infoIcon() },
  };
 
  const { bg, color, icon } = colors[type] || colors.default;
 
  Object.assign(toast.style, {
    display:       "inline-flex",
    alignItems:    "center",
    gap:           "8px",
    background:    bg,
    color:         color,
    fontSize:      "13px",
    fontWeight:    "600",
    fontFamily:    "'DM Sans','Segoe UI',system-ui,sans-serif",
    padding:       "12px 20px",
    borderRadius:  "12px",
    boxShadow:     "0 8px 24px rgba(15,23,42,0.3)",
    whiteSpace:    "nowrap",
    pointerEvents: "auto",
    opacity:       "0",
    transform:     "translateY(12px)",
    transition:    "opacity 0.28s ease, transform 0.28s ease",
    maxWidth:      "90vw",
    overflow:      "hidden",
    textOverflow:  "ellipsis",
  });
 
  // El icono es nuestro (SVG fijo); el MENSAJE no: a menudo trae texto de fuera —errores del
  // servidor, nombres de alojamientos, lo que escribió un huésped— y pintarlo con innerHTML
  // dejaba ejecutar código en el panel del anfitrión. Se pinta como texto. Solo se respetan
  // los <br> con los que algunas pantallas separan líneas.
  toast.innerHTML = icon;
  const texto = document.createElement("span");
  String(message ?? "").split(/<br\s*\/?>/i).forEach((linea, i) => {
    if (i > 0) texto.appendChild(document.createElement("br"));
    texto.appendChild(document.createTextNode(linea));
  });
  toast.appendChild(texto);
  container.appendChild(toast);
 
  // Animate in
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      toast.style.opacity   = "1";
      toast.style.transform = "translateY(0)";
    });
  });
 
  const dismiss = () => {
    toast.style.opacity   = "0";
    toast.style.transform = "translateY(12px)";
    _activeMessages.delete(dedupeKey);
    toast.addEventListener("transitionend", () => toast.remove(), { once: true });
  };

  // Animate out & remove
  const timer = setTimeout(dismiss, duration);

  // Click to dismiss
  toast.addEventListener("click", () => {
    clearTimeout(timer);
    dismiss();
  });
}
 
// ── SVG Icons ──────────────────────────────────────────────────────
function checkIcon() {
  return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" style="flex-shrink:0">
    <path d="M20 6L9 17L4 12" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}
function xIcon() {
  return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" style="flex-shrink:0">
    <path d="M18 6L6 18M6 6l12 12" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
  </svg>`;
}
function warnIcon() {
  return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" style="flex-shrink:0">
    <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}
function infoIcon() {
  return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" style="flex-shrink:0">
    <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2"/>
    <path d="M12 8v4M12 16h.01" stroke="white" stroke-width="2" stroke-linecap="round"/>
  </svg>`;
}
 
// ── API pública ──────────────────────────────────────────────────
export const $toast = {
  success(msg, duration)  { createToast({ message: msg, type: "success", duration }); },
  error(msg, duration)    { createToast({ message: msg, type: "error",   duration }); },
  warning(msg, duration)  { createToast({ message: msg, type: "warning", duration }); },
  info(msg, duration)     { createToast({ message: msg, type: "info",    duration }); },
  show(msg, duration)     { createToast({ message: msg, type: "default", duration }); },
};
